import React, { useContext } from "react";
import { Modal, Input } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import useAxiosInstance from "../../lib/useAxiosInstance";
import YojnaTrackerContext from "../../context/yojnaTrackerContext";
import ErrorMessage from "../Forms/ErrorMessage";

const { TextArea } = Input;

const splitLines = (raw) =>
  String(raw || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

const parseLinks = (raw) => {
  const lines = splitLines(raw);
  return lines.map((line) => {
    const urlMatch = line.match(/https?:\/\/\S+/);
    const url = urlMatch ? urlMatch[0].replace(/[),.]+$/, "") : null;
    let label = url
      ? line.replace(url, "").replace(/[:\-–]\s*$/, "").trim()
      : line.replace(/^[:\-–]\s*/, "").trim();
    if (!label) label = url;
    return { label, url };
  });
};

const linksToText = (links) =>
  (links || [])
    .map((l) => (l.url ? `${l.label && l.label !== l.url ? l.label + " " : ""}${l.url}` : l.label))
    .join("\n");

const FormSchema = Yup.object().shape({
  scheme: Yup.string().trim().required("Scheme name is required").min(3, "Too short"),
  overview: Yup.string().trim().required("Scheme overview is required").min(10, "Too short"),
});

const YojnaTrackerForm = () => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const yojnaContext = useContext(YojnaTrackerContext);
  const { isAddMode, editData } = yojnaContext;

  const closeModal = () => yojnaContext.updateOpenModal(false);

  const formik = useFormik({
    initialValues: {
      scheme: isAddMode ? "" : editData.scheme || "",
      launchDateRaw: isAddMode ? "" : editData.launchDateRaw || "",
      category: isAddMode ? "" : editData.category || "",
      overview: isAddMode ? "" : editData.overview || "",
      achievementsIndia: isAddMode ? "" : (editData.achievementsIndia || []).join("\n"),
      achievementsUP: isAddMode ? "" : (editData.achievementsUP || []).join("\n"),
      additional: isAddMode ? "" : editData.additional || "",
      links: isAddMode ? "" : linksToText(editData.links),
    },
    enableReinitialize: true,
    validationSchema: FormSchema,
    onSubmit: (values) => handleSubmit(values),
  });

  const buildPayload = (values) => ({
    scheme: values.scheme.trim(),
    launchDateRaw: values.launchDateRaw.trim(),
    category: values.category.trim(),
    overview: values.overview,
    achievementsIndia: splitLines(values.achievementsIndia),
    achievementsUP: splitLines(values.achievementsUP),
    additional: values.additional,
    links: parseLinks(values.links),
  });

  const onSuccess = (response) => {
    toast.success(response?.message || "Saved successfully");
    queryClient.invalidateQueries("yojnaTrackerSchemes");
    closeModal();
  };
  const onError = (error) => {
    toast.error(error?.response?.data?.message || "Something went wrong.");
  };

  const createMutation = useMutation(
    (payload) => axiosInstance.post("api/yojna-tracker", payload).then((r) => r.data),
    { onSuccess, onError }
  );
  const updateMutation = useMutation(
    (payload) => axiosInstance.put(`api/yojna-tracker/${editData._id}`, payload).then((r) => r.data),
    { onSuccess, onError }
  );

  const handleSubmit = (values) => {
    const payload = buildPayload(values);
    if (isAddMode) createMutation.mutate(payload);
    else updateMutation.mutate(payload);
  };

  const saving = createMutation.isLoading || updateMutation.isLoading;

  return (
    <Modal
      open
      onCancel={closeModal}
      footer={null}
      width={720}
      maskClosable={false}
      className="yt-form-modal"
      closeIcon={null}
    >
      <div className="yt-modal-head yt-form-head">
        <div className="yt-modal-head-actions">
          <button className="yt-icon-btn" title="Close" onClick={closeModal}>✕</button>
        </div>
        <span className="yt-cat-tag" style={{ background: "#556070" }}>
          {isAddMode ? "New scheme" : "Editing"}
        </span>
        <h2>{isAddMode ? "Add a new scheme" : editData.scheme}</h2>
      </div>
      <form className="yt-edit-form" onSubmit={formik.handleSubmit}>
        <div className="yt-field-group">
          <label>Scheme name</label>
          <Input name="scheme" value={formik.values.scheme} onChange={formik.handleChange} />
          <ErrorMessage
            hasError={Boolean(formik.errors.scheme && formik.touched.scheme)}
            message={formik.errors.scheme}
          />
        </div>
        <div className="yt-field-row">
          <div className="yt-field-group">
            <label>Launch date</label>
            <Input
              name="launchDateRaw"
              placeholder="e.g. 5th January 2015"
              value={formik.values.launchDateRaw}
              onChange={formik.handleChange}
            />
          </div>
          <div className="yt-field-group">
            <label>Category</label>
            <Input
              name="category"
              placeholder="Leave blank to auto-tag"
              value={formik.values.category}
              onChange={formik.handleChange}
            />
          </div>
        </div>
        <div className="yt-field-group">
          <label>Scheme overview</label>
          <TextArea rows={4} name="overview" value={formik.values.overview} onChange={formik.handleChange} />
          <ErrorMessage
            hasError={Boolean(formik.errors.overview && formik.touched.overview)}
            message={formik.errors.overview}
          />
        </div>
        <div className="yt-field-group">
          <label>Achievements — all-India (one point per line)</label>
          <TextArea
            rows={4}
            name="achievementsIndia"
            value={formik.values.achievementsIndia}
            onChange={formik.handleChange}
          />
        </div>
        <div className="yt-field-group">
          <label>Achievements — Uttar Pradesh (one point per line)</label>
          <TextArea
            rows={4}
            name="achievementsUP"
            value={formik.values.achievementsUP}
            onChange={formik.handleChange}
          />
        </div>
        <div className="yt-field-group">
          <label>Additional notes</label>
          <TextArea rows={2} name="additional" value={formik.values.additional} onChange={formik.handleChange} />
        </div>
        <div className="yt-field-group">
          <label>Related links (one per line)</label>
          <TextArea rows={4} name="links" value={formik.values.links} onChange={formik.handleChange} />
          <p className="yt-field-hint">
            Include a full http(s):// URL on a line to make it clickable. Plain text lines show as a source label.
          </p>
        </div>
        <div className="yt-form-actions">
          <button type="submit" className="yt-btn yt-btn-primary" disabled={saving}>
            {isAddMode ? "Add scheme" : "Save changes"}
          </button>
          <button type="button" className="yt-btn yt-btn-cancel" onClick={closeModal}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default YojnaTrackerForm;
